import React, { useState, useEffect, useCallback } from 'react';
import { 
  Package, Truck, CheckCircle, Clock, AlertCircle, 
  RefreshCw, MapPin, ExternalLink, Tag
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Timeline stage icons
const STAGE_ICONS = {
  0: Tag,           // Label generated
  1: Package,       // Admitted
  2: Truck,         // In transit
  3: Truck,         // Arrived at hub
  4: Truck,         // Out for delivery
  5: CheckCircle,   // Delivered
  '-1': AlertCircle // Issue
};

// Stage colors
const STAGE_COLORS = {
  0: 'bg-gray-400',
  1: 'bg-blue-500',
  2: 'bg-blue-500',
  3: 'bg-yellow-500',
  4: 'bg-orange-500',
  5: 'bg-green-500',
  '-1': 'bg-red-500'
};

const TrackingTimeline = ({ trackingNumber, warrantyId, type = 'inbound' }) => {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchTracking = useCallback(async () => {
    if (!trackingNumber && !warrantyId) return;
    
    try {
      let url;
      if (warrantyId) {
        url = `${API_URL}/api/tracking/warranty/${warrantyId}`;
      } else {
        url = `${API_URL}/api/tracking/${trackingNumber}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setTracking(warrantyId ? data[type] : data);
        setError(null);
      } else {
        setError('No se pudo obtener la información de tracking');
      }
    } catch (e) {
      setError('Error de conexión');
    }
    setLoading(false);
  }, [trackingNumber, warrantyId, type]);

  useEffect(() => {
    fetchTracking();
  }, [fetchTracking]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(
        `${API_URL}/api/tracking/refresh/${trackingNumber || tracking?.tracking_number}`,
        { method: 'POST' }
      );
      if (res.ok) {
        await fetchTracking();
      }
    } catch (e) {
      console.error('Error refreshing:', e);
    }
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error && !tracking) {
    return (
      <div className="text-center py-8 text-gray-500">
        <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-400" />
        <p>{error}</p>
      </div>
    );
  }

  if (!tracking) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Package className="w-8 h-8 mx-auto mb-2" />
        <p>No hay información de tracking disponible</p>
      </div>
    );
  }

  const currentStage = tracking.current_stage || 0;
  const StageIcon = STAGE_ICONS[currentStage] || Clock;

  return (
    <div className="space-y-4" data-testid="tracking-timeline">
      {/* Current Status Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${STAGE_COLORS[currentStage]} text-white`}>
                <StageIcon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{tracking.current_status}</h3>
                <p className="text-sm text-gray-500">
                  Tracking: <span className="font-mono">{tracking.tracking_number}</span>
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {tracking.from_cache && (
                <Badge variant="secondary" className="text-xs">Caché</Badge>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open(`https://www.correos.es/es/es/herramientas/localizador/envios/${tracking.tracking_number}`, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Ver en Correos
              </Button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between mb-2">
              {['Registrado', 'Admitido', 'En tránsito', 'En destino', 'Reparto', 'Entregado'].map((label, idx) => (
                <div 
                  key={idx} 
                  className={`text-xs text-center flex-1 ${idx <= currentStage ? 'text-blue-600 font-medium' : 'text-gray-400'}`}
                >
                  {label}
                </div>
              ))}
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 rounded-full transition-all duration-500"
                style={{ width: `${Math.max(0, Math.min(100, (currentStage / 5) * 100))}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events Timeline */}
      {tracking.events && tracking.events.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Historial de Eventos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {tracking.events.slice(0, 10).map((event, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="relative">
                    <div className={`w-3 h-3 rounded-full ${idx === 0 ? 'bg-blue-500' : 'bg-gray-300'}`} />
                    {idx < tracking.events.length - 1 && (
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-0.5 h-full bg-gray-200" />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm ${idx === 0 ? 'font-medium' : 'text-gray-600'}`}>
                        {event.description}
                      </p>
                      <Badge variant="outline" className="text-xs ml-2">
                        {event.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {event.date}
                      </span>
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Last update */}
      {tracking.last_update && (
        <p className="text-xs text-gray-400 text-center">
          Última actualización: {new Date(tracking.last_update).toLocaleString('es-ES')}
        </p>
      )}
    </div>
  );
};

// Mini tracking badge component
export const TrackingBadge = ({ trackingNumber, status, onClick }) => {
  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-600';
    if (status.includes('Entregado')) return 'bg-green-100 text-green-700';
    if (status.includes('Tránsito') || status.includes('Reparto')) return 'bg-blue-100 text-blue-700';
    if (status.includes('Incidencia') || status.includes('Devuelto')) return 'bg-red-100 text-red-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors hover:opacity-80 ${getStatusColor(status)}`}
    >
      <Truck className="w-4 h-4" />
      <span className="font-mono text-xs">{trackingNumber}</span>
    </button>
  );
};

export default TrackingTimeline;
